import { Model } from "./index";
import { extractIds, idify, extractId } from "./_helpers";
import { cheapoClone } from "../../util/func";

export class Project extends Model {
  constructor(data = {}) {
    super({
      id: data?.id ?? null,
      uuid: data?.uuid ?? null,
      name: data?.name ?? null,
      title_web: data?.title_web ?? null,
      slug: data?.slug ?? null,
      code: data?.code ?? null,
      touched: data?.touched ?? null,
      name_server: data?.name_server ?? null,
      city: data?.city ?? null,
      country: data?.country ?? null,
      address: data?.address ?? null,
      zipcode: data?.zipcode ?? null,
      description_web: data?.description_web ?? null,
      in_progress: data?.in_progress ?? null,
      lat: data?.lat ?? null,
      lng: data?.lng ?? null,
      cost: data?.cost ?? null,
      statut_n: data?.statut_n ?? null,
      croppings: data?.croppings ?? "",
      surface: data?.surface ?? null,
      main_image: data?.main_image ?? null,
      web_image: data?.web_image ?? null,
      program_description: data?.program_description ?? null,
      program_scalene: data?.program_scalene ?? null,
      typologies: extractIds(data?.typologies ?? []),
      programs: extractIds(data?.programs ?? []),
      missions: extractIds(data?.missions ?? []),
      labels: extractIds(data?.labels ?? []),
      awards: extractIds(data?.awards ?? []),
      normes: extractIds(data?.normes ?? []),
      contents: data?.contents.map(ContentBlock) ?? [ContentBlock()],
      dates: addMissingDefaultDates(
        data?.id ?? null,
        decideOnDefaultDates(data?.id ?? null, data?.dates ?? null)
      ),
      surfaces: addMissingDefaultSurfaces(
        data?.id ?? null,
        decideOnDefaultSurfaces(data?.id ?? null, data?.surfaces ?? null)
      ),
      interlocuteurs: addMissingDefaultInterlocuteurs(
        data?.id ?? null,
        remapInterlocuteursToIds(
          decideOnDefaultInterlocuteurs(
            data?.id ?? null,
            data?.interlocuteurs ?? null
          )
        )
      ),
      procedures: extractIds(data?.procedures ?? [])
    });
  }
}

/**
 *
 * @returns {{text: string, axe_id: number, pictures: Array}}
 * @constructor
 */
export const ContentBlock = api_block => ({
  axe_id: api_block?.axe_id ?? null,
  pictures: api_block?.pictures.map(PictureForContentBlock) ?? [],
  text: api_block?.text ?? ""
});

/**
 *
 * @returns {{picture_id: null, text: string}}
 * @constructor
 */
export const PictureForContentBlock = api_picture => ({
  picture_id: api_picture?.picture_id ?? null,
  text: api_picture?.text ?? "",
  plan: api_picture?.plan ?? false,
  scale: api_picture?.scale ?? "1/100",
  orientation: api_picture?.orientation ?? "0°"
});

/**
 *
 * @param projectId
 * @param roleId
 * @returns {{role: *, partner: *, contact: *, project: *, statut: *}}
 * @constructor
 */
export const DefaultSparseInterlocuteur = (projectId, roleId = null) => ({
  role: idify(roleId),
  project: idify(projectId),
  statut: idify(null),
  contact: idify(null),
  partner: idify(null)
});

/**
 *
 * @param interlocuteur
 * @returns {string}
 */
export const deriveKeyFromInterlocuteur = interlocuteur =>
  Object.keys(interlocuteur).reduce((str, key) => {
    return `${str}-${
      typeof interlocuteur[key] === "number"
        ? interlocuteur[key]
        : interlocuteur[key].id
    }`;
  }, "");

/**
 *
 * @param projectId
 * @param roleId
 * @returns {{role: *, partner: *, contact: *, project: *, statut: *}}
 * @constructor
 */
export const DefaultScaleneInterlocuteur = (projectId, roleId) => ({
  role: idify(roleId),
  project: idify(projectId),
  statut: idify(null),
  contact: idify(null),
  partner: idify(1)
});

/**
 *
 * @param projectId
 * @returns {*[]}
 * @constructor
 */
export const DefaultInterlocuteurs = (projectId = null) => [
  DefaultSparseInterlocuteur(projectId, 1), // Architecte Mandataire
  DefaultSparseInterlocuteur(projectId, 2), // Responsable
  DefaultSparseInterlocuteur(projectId, 4), // Assistant
  DefaultSparseInterlocuteur(projectId, 5), // Aménageur
  DefaultSparseInterlocuteur(projectId, 9), // Architecte Associé
  DefaultSparseInterlocuteur(projectId, 8), // Acousticien
  DefaultSparseInterlocuteur(projectId, 19), // BET TCE
  DefaultSparseInterlocuteur(projectId, 23), // BET Fluides
  DefaultSparseInterlocuteur(projectId, 22), // BET HQE
  DefaultSparseInterlocuteur(projectId, 16) // Mobilier
];

export const DefaultDate = (projectId, dateId) => ({
  project_id: projectId,
  date_id: dateId,
  content: null
});

/**
 *
 * @param projectId
 * @param surfaceId
 * @return {{surface_id: *, project_id: *, content: null}}
 * @constructor
 */
export const DefaultSurface = (projectId, surfaceId) => ({
  project_id: projectId,
  surface_id: surfaceId,
  content: null
});

/**
 *
 * @param projectId
 * @return {{surface_id, project_id, content}[]}
 * @constructor
 */
export const DefaultSurfaces = (projectId = null) => [
  DefaultSurface(projectId, 1),
  DefaultSurface(projectId, 2)
];

/**
 *
 * @param projectId
 * @returns {*[]}
 * @constructor
 */
export const DefaultDates = (projectId = null) => [
  DefaultDate(projectId, 1),
  DefaultDate(projectId, 2),
  DefaultDate(projectId, 3),
  DefaultDate(projectId, 4),
  DefaultDate(projectId, 5),
  DefaultDate(projectId, 6),
  DefaultDate(projectId, 7),
  DefaultDate(projectId, 8),
  DefaultDate(projectId, 9),
  DefaultDate(projectId, 10),
  DefaultDate(projectId, 11),
  DefaultDate(projectId, 12)
];

/**
 *
 * @param interlocuteur
 * @returns {{}}
 */
export const remapInterlocuteurToIds = interlocuteur => {
  return Object.keys(interlocuteur).reduce((mapped, key) => {
    if (key.indexOf("id") !== -1) {
      mapped[key] = interlocuteur[key];
      return mapped;
    }
    mapped[`${key}_id`] = extractId(interlocuteur[key]);
    return mapped;
  }, {});
};

/**
 *
 * @param projectId
 * @param interlocuteurs
 * @returns {[]}
 */
export const addMissingDefaultInterlocuteurs = (projectId, interlocuteurs) => {
  const defaults = DefaultInterlocuteurs(projectId);
  const availableRoles = interlocuteurs.map(i => i.role_id);
  const defaultRoles = defaults.map(i => i.role.id);
  const missing = defaultRoles.reduce((acc, r) => {
    if (availableRoles.indexOf(r) === -1) acc.push(r);
    return acc;
  }, []);
  missing.forEach(m => {
    interlocuteurs.push(
      remapInterlocuteurToIds(defaults.find(a => a.role.id === m))
    );
  });
  return interlocuteurs;
};

/**
 *
 * @param projectId
 * @param interlocuteurs
 * @returns {*}
 */
export const decideOnDefaultInterlocuteurs = (projectId, interlocuteurs) =>
  !interlocuteurs || interlocuteurs.length === 0
    ? DefaultInterlocuteurs(projectId)
    : interlocuteurs;

/**
 *
 * @param projectId
 * @param dates
 * @returns {*[]}
 */
export const decideOnDefaultDates = (projectId, dates) => {
  return !dates || dates.length === 0 ? DefaultDates(projectId) : dates;
};

/**
 *
 * @param projectId
 * @param dates
 * @returns {*}
 */
export const addMissingDefaultDates = (projectId, dates) => {
  const defaults = DefaultDates(projectId);
  const availableDates = dates.map(d => d.date_id);
  const defaultDates = defaults.map(d => d.date_id);
  const missing = defaultDates.reduce((acc, c) => {
    if (availableDates.indexOf(c) === -1) acc.push(c);
    return acc;
  }, []);
  missing.forEach(m => {
    dates.push(defaults.find(a => a.date_id === m));
  });
  return dates;
};

/**
 *
 * @param projectId
 * @param surfaces
 * @returns {*[]}
 */
export const decideOnDefaultSurfaces = (projectId, surfaces) => {
  return !surfaces || surfaces.length === 0
    ? DefaultSurfaces(projectId)
    : surfaces;
};

/**
 *
 * @param projectId
 * @param surfaces
 * @returns {*}
 */
export const addMissingDefaultSurfaces = (projectId, surfaces) => {
  const defaults = DefaultSurfaces(projectId);
  const availableSurfaces = surfaces.map(d => d.surface_id);
  const defaultSurfaces = defaults.map(d => d.surface_id);
  const missing = defaultSurfaces.reduce((acc, c) => {
    if (availableSurfaces.indexOf(c) === -1) acc.push(c);
    return acc;
  }, []);
  missing.forEach(m => {
    surfaces.push(defaults.find(a => a.surface_id === m));
  });
  return surfaces;
};

/**
 *
 * @param arr
 * @returns {*}
 */
export const remapInterlocuteursToIds = arr => arr.map(remapInterlocuteurToIds);

/**
 *
 */

/**
 *
 * @param dates
 * @returns {*}
 */
export const filterBadDates = dates =>
  dates.filter(d => d.content !== null && d.content !== "0000-00-00");

/**
 *
 * @param interlocuteurs
 * @returns {*}
 */
export const filterBadInterlocuteurs = interlocuteurs =>
  interlocuteurs
    .filter(i => {
      return (
        i.project_id !== null &&
        i.contact_id !== null &&
        i.statut_id !== null &&
        i.role_id !== null &&
        i.partner_id !== null
      );
    })
    .map(a => {
      delete a.id;
      return a;
    });

/**
 *
 * @param project
 */
export const prepareProjectBeforeSend = project => {
  const p = cheapoClone(project);
  p.interlocuteurs = filterBadInterlocuteurs(p.interlocuteurs);
  p.dates = filterBadDates(p.dates);
  return p;
};
